import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import {
  IRadioButtonsQuestionSchema,
  IRadioButtonsResponseValidation,
  RadioButtonsQuestionModelTypes,
  RadioButtonsResponse
} from './types'

import { getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import LoaderView from '../../actions/loader/View'
import usePreloadImages from '../../modules/images/usePreloadImages'
import useTimeline from '../../modules/timeline/useTimeline'
import useSession from '../../session/useSession'
import usePreviewOptions from '../../modules/preview/usePreviewOptions'
import imageAssetWithOptions from '../../modules/preview/functions/imageAssetWithOptions'

import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import RadioButtonsView from './View'
import VideoBlock from '../_common/VideoBlock/VideoBlock'

const RadioButtonsQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<RadioButtonsQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, frame], setResponse] = useState<
      [RadioButtonsResponse, IRadioButtonsResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])
    const [showValidation, setShowValidation] = useState(false)

    const session = useSession()
    const timeline = useTimeline()
    const previewOptions = usePreviewOptions()
    const imagesPreloaded = usePreloadImages(model)
    const [videoWatched, setVideoWatched] = useState(!model.schema.videos?.length)
    const schema = useMemo(() => {
      const schema = JSON.parse(JSON.stringify(model.schema)) as IRadioButtonsQuestionSchema
      // schema.parentId = schema.id.split('sq')[0]
      schema.answers.forEach(a => {
        a.image = imageAssetWithOptions(session.rules?.sub?.[schema.id]?.[a.id], a.image)
        a.fakeHidden =
          previewOptions.showHidden && false == session.rules?.show?.[a.id] && !previewOptions.suppressLogic
        a.hidden = !previewOptions.showHidden && false == session.rules?.show?.[a.id]
        return a
      })
      if (!schema.answers.some(a => !a.hidden && !a.fakeHidden)) session.enableNext(true)
      return schema
    }, [model.schema, session, previewOptions.showHidden, previewOptions.suppressLogic])

    useEffect(() => {
      session.enableNext(
        (responseValidation.valid || !schema.answers.some(a => !a.hidden && !a.fakeHidden)) && videoWatched
      )
    }, [session, responseValidation.valid, schema.answers, videoWatched])

    const onUpdateResponse = (response: RadioButtonsResponse) => {
      model.response = response
      setResponse([model.response, model.responseValidation, model.frame])
      setShowValidation(false)
    }

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(ref, () => {
      return {
        tryNext() {
          if (
            model.responseValidation.valid ||
            session.skipValidation ||
            !schema.answers.some(a => !a.hidden && !a.fakeHidden)
          ) {
            return false
          } else {
            session.enableNext(false)
            return true
          }
        },
        tryPrev() {
          return false
        },
        showValidation() {
          setShowValidation(true)
        }
      }
    }, [model.responseValidation.valid, schema.answers, session])

    // const videoEmbedding = !model.schema.image_grid_columns && model.schema?.video?.url ? model.schema?.video : null

    const imageRef = useRef<string[]>(null)
    if (!imageRef.current)
      imageRef.current = model.schema.answers.reduce((array: string[], answer) => {
        if (model.schema.image_grid_columns || (answer?.image && !answer.image.expand))
          array.push(
            model.schema.image_grid_columns ? getMediumAssetPath(answer.image) : getSmallAssetPath(answer.image)
          )
        return array
      }, [])

    return (
      <QuestionFrame model={model} images={imageRef.current}>
        <VideoBlock
          videos={model.schema.videos}
          media_mask={model.schema.media_mask}
          id={model.schema.id}
          showValidationError={showValidation}
          onWatched={() => setVideoWatched(true)}
        />
        {imagesPreloaded ? (
          <RadioButtonsView
            schema={schema}
            response={response}
            responseValidation={responseValidation}
            frame={frame}
            timeline={timeline}
            showValidation={showValidation}
            onUpdateResponse={onUpdateResponse}
          />
        ) : (
          <LoaderView />
        )}
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

RadioButtonsQuestion.displayName = 'RadioButtonsQuestion'

export default RadioButtonsQuestion
